import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPinIcon, ArrowTopRightOnSquareIcon, PhoneIcon } from '@heroicons/react/24/outline';
import Modal from '../../shared/components/UI/Modal';
import * as API from '../../api';

const InfoEventModal = ({ shown, close, order }) => {
  const { t } = useTranslation();
  if (!Object.keys(order).length) return <Modal shown={shown} close={close} size="modal--large" />;

  const event = order.event;
  const info = API.OrderInfo(order);
  const total = API.priceFormat(info.totalPriceByDiscount);
  const totalPay = API.priceFormat(info.totalPricePay);
  const theRest = API.priceFormat(info.totalPriceRest);
  const hasDeposit = Math.round(info.totalPricePay) > 0;

  const coupon = parseInt(order.coupon_value)
    ? { value: order.coupon_value, price: API.priceFormat(info.totalPriceByDiscount - info.totalPrice) }
    : null;
  const userDiscount = parseInt(order.customer_discount)
    ? { value: order.customer_discount, price: API.priceFormat(info.totalPriceByDiscount - info.totalPrice) }
    : null;

  return (
    <Modal shown={shown} close={close} size="modal--large" title={event.title}>
      <div className="space-y-5">
        {/* Header: image + key facts */}
        <div className="flex flex-col gap-4 sm:flex-row">
          {event.image ? (
            <img src={event.image} alt={event.title} className="h-40 w-full shrink-0 rounded-xl object-cover sm:h-32 sm:w-44" />
          ) : null}
          <dl className="flex-1 space-y-2 text-sm">
            <Row label={t('account.infoDepartureCity')} value={order.depart_city} />
            {info.roader ? <Row label={t('account.infoCarpool')} value={info.roader} /> : null}
            <Row label={t('account.infoGuests')} value={info.totalPlaces} />
            {event.type === 'Activitie' ? (
              <>
                <Row label={t('account.infoReservationDate')} value={order.date_activity} />
                {parseInt(order.hour_status || event.hour_status) ? (
                  <Row label={t('account.infoSlot')} value={order.time_activity} />
                ) : null}
              </>
            ) : null}
            {event.type === 'Event' ? (
              <>
                <Row label={t('account.infoDepartDate')} value={new Date(event.date_depart).toLocaleDateString('fr-FR')} />
                <Row label={t('account.infoDepartHour')} value={event.hour_depart} />
              </>
            ) : null}
          </dl>
        </div>

        {/* Price lines */}
        {order.prices?.length ? (
          <div className="rounded-xl border border-slate-100 bg-surface-muted p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">{t('account.infoPrices')}</p>
            <ul className="space-y-1.5 text-sm">
              {order.prices.map((item, i) => {
                const qt = item.qt ?? item.quantities;
                return (
                  <li key={i} className="flex items-center justify-between gap-3 text-ink-muted">
                    <span>
                      {item.name} · {API.priceFormat(item.price)} × {qt}
                    </span>
                    <bdi className="font-medium text-ink">{API.priceFormat(parseFloat(item.price) * qt)}</bdi>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {/* Totals */}
        <div className="space-y-2 border-t border-slate-100 pt-4 text-sm">
          {userDiscount ? (
            <div className="flex items-center justify-between text-emerald-600">
              <span>{t('account.infoWelcomeDiscount')} -{userDiscount.value}%</span>
              <bdi>{userDiscount.price}</bdi>
            </div>
          ) : coupon ? (
            <div className="flex items-center justify-between text-emerald-600">
              <span>{t('account.infoCouponDiscount')} -{coupon.value}%</span>
              <bdi>{coupon.price}</bdi>
            </div>
          ) : null}
          <div className="flex items-center justify-between">
            <span className="font-display text-base font-semibold text-ink">{t('account.infoTotal')}</span>
            <bdi className="font-display text-lg font-bold text-brand-500">{total}</bdi>
          </div>
          {hasDeposit ? (
            <div className="mt-2 space-y-1 rounded-xl bg-surface-muted p-3 text-xs">
              <div className="flex items-center justify-between text-ink">
                <span>{t('account.infoAdvance')}</span>
                <bdi className="font-semibold">{totalPay}</bdi>
              </div>
              <div className="flex items-center justify-between text-ink-subtle">
                <span>{t('account.infoOnSite')}</span>
                <bdi>{theRest}</bdi>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between text-ink-muted">
              <span>{t('account.infoOnSite')}</span>
              <bdi>{theRest}</bdi>
            </div>
          )}
        </div>

        {/* Location / meeting */}
        {event.activityPlace ? (
          <Section title={t('account.infoLocationTitle')}>
            <a
              href={event.activityPlace}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-500 hover:underline"
            >
              <MapPinIcon className="h-4 w-4" />
              {t('account.infoLocationLink')}
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </a>
            {event.activityPlaceDesc ? (
              <div className="mt-2 text-sm text-ink-muted" dangerouslySetInnerHTML={{ __html: event.activityPlaceDesc }} />
            ) : null}
          </Section>
        ) : null}
        {event.activityMeetDesc ? (
          <Section title={t('account.infoMeetingTitle')}>
            <div className="text-sm text-ink-muted" dangerouslySetInnerHTML={{ __html: event.activityMeetDesc }} />
          </Section>
        ) : null}

        {/* Organizer */}
        <Section title={t('account.infoOrganizer')}>
          <div className="space-y-1 text-sm">
            <Row label={t('account.infoOrganizerName')} value={order.partner} />
            {order.partner_phone ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-ink-muted">{t('account.infoOrganizerPhone')}</span>
                <a href={`tel:${order.partner_phone}`} className="inline-flex items-center gap-1.5 font-medium text-brand-500 hover:underline">
                  <PhoneIcon className="h-4 w-4" />
                  {order.partner_phone}
                </a>
              </div>
            ) : null}
          </div>
        </Section>
      </div>
    </Modal>
  );
};

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right font-medium text-ink rtl:text-left">{value}</dd>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4">
      <h4 className="mb-2 font-display text-sm font-semibold text-ink">{title}</h4>
      {children}
    </div>
  );
}

export default InfoEventModal;
